import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { GlassCard } from "../components/GlassCard";
import { Copy, Check } from "lucide-react";

export default function Login() {
  const [step, setStep] = useState(1); // 1=creds, 2=totp login, 3=totp setup (first login)
  const [form, setForm] = useState({ email: "", password: "" });
  const [pre2fa, setPre2fa] = useState(null);
  const [setupToken, setSetupToken] = useState(null);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sessionNotice, setSessionNotice] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const from = location.state?.from?.pathname || "/";

  // Surface the reason the session ended (from /login?reason=...), set by the
  // axios 401 interceptor on token expiry / signed-in-elsewhere.
  useEffect(() => {
    const reason = params.get("reason");
    if (reason) setSessionNotice(decodeURIComponent(reason));
  }, [params]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.setup_required) {
        setSetupToken(data.setup_token);
        setQr(data.qr_png_base64);
        setSecret(data.secret);
        setCode("");
        setStep(3);
      } else {
        setPre2fa(data.pre_2fa_token);
        setCode("");
        setStep(2);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(
        "/auth/login-2fa",
        { code },
        { headers: { Authorization: `Bearer ${pre2fa}` } }
      );
      login(data.access_token, data.user);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(
        "/auth/verify-2fa-setup",
        { code },
        { headers: { Authorization: `Bearer ${setupToken}` } }
      );
      login(data.access_token, data.user);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div
            className="mx-auto w-14 h-14 rounded-2xl mb-4"
            style={{
              background: "linear-gradient(135deg,#D4B487 0%, #C9A876 60%, #8F6D3F 100%)",
              boxShadow:
                "0 8px 22px rgba(184,147,90,0.35), inset 0 1px 0 rgba(255,255,255,0.6)",
            }}
          >
            <img src="/logo.svg" alt="Solartoner" className="w-14 h-14" />
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-tight">Solartoner</h1>
          <p className="text-stone-500 mt-3">Sign in to your workspace.</p>
        </div>

        <GlassCard>
          {step === 1 && (
            <form onSubmit={submitCreds} className="space-y-4">
              <div>
                <label className="label-tiny">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  className="w-full mt-1 px-4 py-3 rounded-2xl bg-white/60 border border-white/80 focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 transition-colors"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="label-tiny">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="w-full mt-1 px-4 py-3 rounded-2xl bg-white/60 border border-white/80 focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 transition-colors"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              {err && (
                <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2">
                  {err}
                </div>
              )}
              {sessionNotice && !err && (
                <div className="text-sm text-amber-800 bg-amber-50/70 border border-amber-200 rounded-xl px-3 py-2" data-testid="session-notice">
                  {sessionNotice}
                </div>
              )}
              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={busy}
                className="btn-gold w-full"
              >
                {busy ? "Signing in…" : "Continue"}
              </button>
              <p className="text-center text-xs text-stone-500 pt-2">
                Access is provided by your administrator.
              </p>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitCode} className="space-y-5">
              <div>
                <h3 className="font-serif text-2xl">Enter your 2FA code</h3>
                <p className="text-sm text-stone-500 mt-1">
                  Open Google Authenticator and enter the 6-digit code.
                </p>
              </div>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  data-testid="login-totp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg bg-white/70" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {err && (
                <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2 text-center">
                  {err}
                </div>
              )}
              <button
                data-testid="login-totp-submit"
                type="submit"
                disabled={busy || code.length !== 6}
                className="btn-gold w-full"
              >
                {busy ? "Verifying…" : "Sign in"}
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitSetup} className="space-y-5">
              <div>
                <h3 className="font-serif text-2xl">Set up 2FA</h3>
                <p className="text-sm text-stone-500 mt-1">
                  Scan this QR in Google Authenticator, then enter the 6-digit code.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="p-3 rounded-2xl bg-white border border-white/90 shadow-[0_8px_28px_rgba(120,90,40,0.1)]">
                  <img
                    src={`data:image/png;base64,${qr}`}
                    alt="TOTP QR"
                    className="w-44 h-44"
                    data-testid="totp-qr"
                  />
                </div>
              </div>
              <div className="text-center">
                <div className="label-tiny">Or enter this secret manually</div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  className="mt-1 inline-flex items-center gap-2 font-mono text-sm bg-white/70 rounded-full px-3 py-1.5 hover:bg-white transition-colors"
                  data-testid="totp-secret-copy"
                >
                  {secret}
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <div className="flex flex-col items-center gap-2">
                <label className="label-tiny">6-digit code</label>
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={setCode}
                  data-testid="totp-code-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg bg-white/70" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {err && (
                <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2 text-center">
                  {err}
                </div>
              )}
              <button
                data-testid="totp-verify-btn"
                type="submit"
                disabled={busy || code.length !== 6}
                className="btn-gold w-full"
              >
                {busy ? "Verifying…" : "Activate & sign in"}
              </button>
            </form>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
